import React from 'react';
import { Redirect } from '@shopify/app-bridge/actions';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Card, Button, ProgressBar, Badge, Text } from '@shopify/polaris';
import { config, DEFAULT_PLAN_NAMES } from '../constants';
import { authenticatedFetch } from '../utils/api';
import { plans, getResolvedFeatures } from '../plans';

const API_BASE = config.API_BASE;



export default function Subscriptions({ shop }) {
  const params = new URLSearchParams(window.location.search);
  const shopFromUrl = params.get('shop');
  const effectiveShop = shop || shopFromUrl;
  const app = useAppBridge();
  const host = params.get('host');

  const [currentPlan, setCurrentPlan] = React.useState(null);
  const [usage, setUsage] = React.useState(null);
  const [nextPayment, setNextPayment] = React.useState(null);
  React.useEffect(() => {
    async function fetchCurrentPlan() {
      try {
        const res = await authenticatedFetch(`${API_BASE}/current-plan?shop=${effectiveShop}`);
        if (!res.ok) throw new Error('Failed to fetch current plan');
        const data = await res.json();
        setCurrentPlan(data.plan);
      } catch (err) {
        setCurrentPlan(null);
      }
    }
    if (effectiveShop) fetchCurrentPlan();
  }, [effectiveShop]);

  React.useEffect(() => {
    async function fetchUsage() {
      try {
        const res = await authenticatedFetch(`${API_BASE}/usage?shop=${effectiveShop}`);
        if (!res.ok) throw new Error('Failed to fetch usage');
        const data = await res.json();
        setUsage(data.usage);
        setNextPayment(data.nextPayment);
      } catch (err) {
        setUsage(null);
        setNextPayment(null);
      }
    }
    if (effectiveShop) fetchUsage();
  }, [effectiveShop]);

  const handleUpgrade = async (planId) => {
    try {
      console.log("handleUpgrade called with shop:", effectiveShop, "planId:", planId, "host:", host);
      const res = await authenticatedFetch(`${API_BASE}/billing-start?shop=${effectiveShop}&plan=${planId}&host=${host}`);
      const data = await res.json();
      if (planId.toLowerCase() === 'free' && data && data.success) {
        // Downgrade to Free: redirect to summary/dashboard page
        window.location.href = `/dashboard?shop=${encodeURIComponent(effectiveShop)}&host=${encodeURIComponent(host)}`;
        return;
      }
      if (data && data.confirmation_url) {
        let confirmationUrlWithHost = data.confirmation_url;
        if (host) {
          confirmationUrlWithHost += (confirmationUrlWithHost.includes('?') ? '&' : '?') + 'host=' + encodeURIComponent(host);
        }
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, confirmationUrlWithHost);
      } else {
        window.location.href = data.confirmation_url || res.url;
      }
    } catch (err) {
      window.location.href = '/error?msg=' + encodeURIComponent(err?.message || 'Unknown error');
    }
  };


  return (
    <div style={{ maxWidth: 1100, margin: '40px auto', padding: 24 }}>
      {/* Subscription Plan Details Card - matches photo 1 */}
      <Card sectioned title="Subscription details" subdued>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 2 }}>{currentPlan ? `${currentPlan} plan` : 'Loading...'}</div>
            <div style={{ color: '#7b819c', fontSize: 15 }}>
              {currentPlan === 'Free' ? '$0/month' : plans.find(p => p.id.toLowerCase() === (currentPlan||'').toLowerCase())?.price + '/month'}
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 120 }}>
            <div style={{ color: '#7b819c', fontWeight: 500 }}>Next payment</div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {nextPayment !== null ? (nextPayment === 0 ? '$0' : `$${nextPayment}`) : '--'}
            </div>
          </div>
        </div>
      </Card>
      <div style={{ height: 24 }} />
      {/* AI Usage Card - matches photo 1 */}
      <Card sectioned title="Automated reply usage" subdued>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#7b819c', marginBottom: 6 }}>Monthly usage</div>
            <div style={{
              width: '100%',
              height: 10,
              background: '#f4f6f8',
              borderRadius: 6,
              overflow: 'hidden',
              margin: '8px 0'
            }}>
              <div style={{
                width: `${usage && usage.limit ? Math.min(100, Math.round((usage.count/usage.limit)*100)) : 0}%`,
                height: '100%',
                background: usage && usage.limit && usage.count/usage.limit > 0.95 ? '#de3618' : '#2a5bd7',
                transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 6
              }} />
            </div>
          </div>
          <div style={{ fontWeight: 600, color: '#23272f', fontSize: 15, minWidth: 66, textAlign: 'right' }}>{usage ? `${usage.count} / ${usage.limit}` : '--'}</div>
        </div>
      </Card>

      {/* Plan Selection Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 28, marginTop: 40 }}>
        {plans.map((plan, i) => {
          const isCurrent = plan.id.toLowerCase() === (currentPlan || '').toLowerCase();
          const planOrder = ['Free', 'Basic', 'Standard', 'Premium'];
          const currentIdx = planOrder.findIndex(p => p.toLowerCase() === (currentPlan || '').toLowerCase());
          const thisIdx = planOrder.findIndex(p => p.toLowerCase() === plan.id.toLowerCase());
          let buttonText = 'Upgrade';
          if (thisIdx < currentIdx) buttonText = 'Downgrade';
          return (
            <Card key={plan.id} sectioned subdued style={{ flex: 1, minWidth: 240, margin: '0 8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 3 }}>{plan.name}</div>
                {plan.highlight && (
                  <Badge status="info">{plan.highlight}</Badge>
                )}
                <div style={{ fontSize: 28, fontWeight: 700, color: '#2a5bd7', marginBottom: 4 }}>
                  {plan.price}
                  <span style={{ fontSize: 17, fontWeight: 500, color: '#222', marginLeft: 2 }}>{plan.priceSuffix}</span>
                </div>
                <Button
                  variant='primary'
                  primary={!isCurrent}
                  outline={isCurrent}
                  disabled={isCurrent}
                  onClick={() => handleUpgrade(plan.id)}
                  fullWidth
                  style={{ margin: '10px 0' }}
                >
                  {isCurrent ? 'Current Plan' : buttonText}
                </Button>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 14, color: '#444', textAlign: 'left', width: '100%' }}>
                  {getResolvedFeatures(plan.id, plans, { uiOnly: true }).map(f => (
                    <li key={f} style={{ margin: '7px 0', display: 'flex', alignItems: 'center' }}>
                      <span role="img" aria-label="check">✔️</span>&nbsp;{f}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
